import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useCallback } from 'react'
import { z } from 'zod'
import { Page } from '~/components'
import { IntegrationHubPage, useCanViewBotIntegrations } from '~/features/integration-hub'
import { useIsAuthorized } from '~/hooks'

const integrationsSearchSchema = z.object({
  view: z.enum(['hub', 'installed']).optional(),
  integration: z.string().optional(),
  category: z.string().optional(),
  q: z.string().optional(),
})

export type IntegrationsSearch = z.infer<typeof integrationsSearchSchema>

export const Route = createFileRoute('/workspaces/$workspaceId/bots/$botId/integrations')({
  component: IntegrationsPage,
  validateSearch: integrationsSearchSchema.parse,
})

const INTEGRATIONS_PAGE_CLASSNAME = 'flex h-[calc(100dvh-6.5rem)] min-h-0 max-w-none flex-col md:h-[calc(100dvh-6rem)]'

function IntegrationsPage() {
  const { workspaceId, botId } = Route.useParams()
  const { user } = Route.useRouteContext()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const authorize = useIsAuthorized({ workspaceId, userId: user.id })
  const canView = useCanViewBotIntegrations({ workspaceId, userId: user.id })

  const updateSearch = useCallback(
    (patch: Partial<IntegrationsSearch>, options?: { replace?: boolean }) => {
      void navigate({
        search: (prev) => {
          const next = { ...prev, ...patch }
          return Object.fromEntries(
            Object.entries(next).filter(([, value]) => value !== undefined && value !== '')
          ) as IntegrationsSearch
        },
        replace: options?.replace ?? false,
      })
    },
    [navigate]
  )

  if (canView === undefined) {
    return (
      <Page className={INTEGRATIONS_PAGE_CLASSNAME}>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Page>
    )
  }

  if (canView === false) {
    return (
      <Page className={INTEGRATIONS_PAGE_CLASSNAME}>
        <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
          You do not have permission to view integrations.
        </div>
      </Page>
    )
  }

  const permissions = {
    canCreate: authorize('integrations.create') ?? false,
    canUpdate: authorize('integrations.update') ?? false,
    canDelete: authorize('integrations.delete') ?? false,
  }

  return (
    <Page className={INTEGRATIONS_PAGE_CLASSNAME}>
      <IntegrationHubPage
        workspaceId={workspaceId}
        botId={botId}
        permissions={permissions}
        urlState={search}
        onUrlStateChange={updateSearch}
      />
    </Page>
  )
}
